import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Plane, Hotel, Car, Map, Mail, Phone, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "quoted", "converted", "closed"];

const STATUS_STYLES = {
  new:       "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  quoted:    "bg-purple-500/15 text-purple-300 border-purple-500/40",
  converted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  closed:    "bg-gray-500/15 text-gray-300 border-gray-500/40",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// Admin lead-management view. Lists enquiries newest first, filterable by
// status, with inline status pill + notes field so the ops team can triage
// without leaving the row.
export default function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [noteDraft, setNoteDraft] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get(`${API}/admin/enquiries`, { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, patch);
      toast.success("Enquiry updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  const includedComponents = (e) => {
    const parts = [];
    if (e.include_flight)    parts.push({ Icon: Plane,  label: "Flight" });
    if (e.include_hotel)     parts.push({ Icon: Hotel,  label: "Hotel" });
    if (e.include_transfer)  parts.push({ Icon: Car,    label: "Transfer" });
    if (e.include_itinerary) parts.push({ Icon: Map,    label: "Itinerary" });
    return parts;
  };

  return (
    <div data-testid="enquiries-tab" className="space-y-4">
      {/* Filter + refresh */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="enquiry-filter-all"
          className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
            filter === "" ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
          }`}
        >
          All ({items.length && !filter ? items.length : ""})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`enquiry-filter-${s}`}
            className={`px-3 py-1.5 text-xs rounded-full border capitalize transition-all ${
              filter === s ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
            }`}
          >
            {s}
          </button>
        ))}
        <div className="flex-1" />
        <Button
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="text-white/70 hover:text-white"
          data-testid="enquiries-refresh-button"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* List */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {items.length === 0 && !loading && (
          <p className="p-6 text-center text-sm text-white/50">No enquiries yet.</p>
        )}
        {items.map((e) => {
          const isOpen = !!expanded[e.id];
          return (
            <div key={e.id} className="border-b border-white/10 last:border-0" data-testid={`enquiry-row-${e.id}`}>
              <button
                type="button"
                onClick={() => setExpanded((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-white/5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium truncate">{e.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>
                      {e.status}
                    </span>
                    {e.wants_emi && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">
                        EMI {e.emi_months ? `${e.emi_months}m` : ""}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/60 mt-1 truncate">
                    {e.destination} · {e.adults + (e.children || 0) + (e.infants || 0)} pax
                    {e.origin_country ? ` · from ${e.origin_city ? `${e.origin_city}, ` : ""}${e.origin_country}` : ""}
                  </div>
                </div>
                <div className="hidden sm:block text-right text-xs text-white/40 shrink-0">{fmtDate(e.created_at)}</div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1.5">
                    <p className="text-white/50 text-xs uppercase tracking-wider">Contact</p>
                    <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 text-white/80 hover:text-white">
                      <Mail className="w-3.5 h-3.5" /> {e.email}
                    </a>
                    <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 text-white/80 hover:text-white">
                      <Phone className="w-3.5 h-3.5" /> {e.phone}
                    </a>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-white/50 text-xs uppercase tracking-wider">Trip</p>
                    <p className="text-white/80">
                      {e.travel_start || "—"} → {e.travel_end || "—"}
                      {e.duration_nights ? ` · ${e.duration_nights} nights` : ""}
                    </p>
                    <p className="text-white/80 capitalize">Package: {e.package_preference || "any"}</p>
                    {e.budget_inr ? (
                      <p className="text-white/80">Budget: ₹{Number(e.budget_inr).toLocaleString("en-IN")}</p>
                    ) : null}
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Include</p>
                    <div className="flex flex-wrap gap-2">
                      {includedComponents(e).map(({ Icon, label }) => (
                        <span key={label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs">
                          <Icon className="w-3 h-3" /> {label}
                        </span>
                      ))}
                      {includedComponents(e).length === 0 && (
                        <span className="text-xs text-white/40">Nothing toggled on</span>
                      )}
                    </div>
                  </div>
                  {e.notes && (
                    <div className="md:col-span-2">
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Notes from customer</p>
                      <p className="text-white/80 text-sm whitespace-pre-wrap">{e.notes}</p>
                    </div>
                  )}
                  <div className="md:col-span-2 pt-3 border-t border-white/5">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Update status</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => update(e.id, { status: s, admin_note: noteDraft[e.id] ?? e.admin_note ?? "" })}
                          disabled={saving[e.id]}
                          data-testid={`enquiry-${e.id}-status-${s}`}
                          className={`px-3 py-1 text-xs rounded-full border capitalize transition-all ${
                            e.status === s
                              ? "bg-white text-black border-white"
                              : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <textarea
                      rows={2}
                      value={noteDraft[e.id] ?? e.admin_note ?? ""}
                      onChange={(ev) => setNoteDraft((prev) => ({ ...prev, [e.id]: ev.target.value }))}
                      placeholder="Internal note (visible to admins only)"
                      className="w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]/50"
                      data-testid={`enquiry-${e.id}-note-input`}
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        size="sm"
                        onClick={() => update(e.id, { status: e.status, admin_note: noteDraft[e.id] ?? e.admin_note ?? "" })}
                        disabled={saving[e.id]}
                        data-testid={`enquiry-${e.id}-save-note`}
                        className="bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Save note
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
